import type { FavoriteSet, FavoriteSets } from '#features/FavoriteSets/FavoriteSet'
import { getSessionState } from '#features/User/userManager'

export const favoriteSetsType = 'maic-frontend-favorite-sets' as const
export const favoriteSetsVersionNumber = 'v0.2.0' as const

export type StoredFavoriteSets = {
  __version: typeof favoriteSetsVersionNumber
  __updatedAt: Timestamp
  __type: typeof favoriteSetsType
  __maicVersion: string
  __userName: string | undefined
  favoriteSets: FavoriteSets
}

export type FavoriteSetsVersionNumber = typeof favoriteSetsVersionNumber

export type FavoriteSetsType = typeof favoriteSetsType

export function isStoredFavoriteSets(storedData: unknown): storedData is StoredFavoriteSets {
  return (
    typeof storedData === 'object' &&
    storedData !== null &&
    '__version' in storedData &&
    '__type' in storedData &&
    storedData.__type === favoriteSetsType
  )
}

export const exportFavoriteSetsAsJson = (favoriteSets: FavoriteSet[]) => {
  // name the thing
  const now = new Date()
  const offset = now.getTimezoneOffset() * 60 * 1000
  const dateString = new Date(now.getTime() - offset).toISOString().split('T')[0]
  const setName = favoriteSets.length === 1 ? favoriteSets[0]?.name : 0

  const authSessionState = getSessionState()
  const user = authSessionState?.user
  const userName = user?.profile ? [user.profile.name, user.profile.given_name, user.profile.family_name].filter(Boolean).join(' ') : undefined

  const filename = ['MAIC', 'favorites', setName, userName, dateString, '.maic-fav']
    .filter(Boolean)
    .join('-')
    .replace(/[^a-zA-Z0-9-_.]/g, '_')

  const storedData: StoredFavoriteSets = {
    __version: favoriteSetsVersionNumber,
    __updatedAt: now.getTime() as Timestamp,
    __type: favoriteSetsType,
    __maicVersion: `v${VITE_APP_VERSION} (${VITE_COMMIT_SHA})`,
    __userName: userName,
    favoriteSets: favoriteSets,
  }

  // get the downloadable data
  const serialized = JSON.stringify(storedData, null, 2)
  const blob = new Blob([serialized], { type: 'application/json' })
  const href = URL.createObjectURL(blob)

  // make it downloadable
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)

  // download and clean it up
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(href)
}
